import { useEffect, useState } from 'react'

import { Box, Button, Flex, Text, VStack } from '@chakra-ui/react'
import Image from 'next/image'
import useTranslation from 'next-translate/useTranslation'

import { Card } from 'components/common/Card'
import { IntegrationImages } from 'constants/integrations'
import {
  IntegrationType,
  useClaimVenueAccountMutation,
} from 'generated/generated-graphql'
import { useErrorToast, useSuccessToast } from 'utils/toast'

type Props = {
  email: string
  integrationType: IntegrationType
  resendAvailableInSeconds: number
  onBackToSignIn: () => void
}

/**
 * Shown on sign in for accounts created by accepting a match deal, the set password email is already sent. The
 * account is claimed only once the venue sets its password from the email and signs in with it.
 */
export const ClaimVenueAccount = ({
  email,
  integrationType,
  resendAvailableInSeconds,
  onBackToSignIn,
}: Props) => {
  const { t } = useTranslation('common')
  const errorToast = useErrorToast()
  const successToast = useSuccessToast()
  const [secondsUntilResend, setSecondsUntilResend] = useState(
    resendAvailableInSeconds
  )
  const [claimVenueAccount, { loading: isClaimVenueAccountLoading }] =
    useClaimVenueAccountMutation()

  const integrationName = t(`Integrations.ProviderNames.${integrationType}`)

  useEffect(() => {
    if (secondsUntilResend <= 0) {
      return
    }

    const timeout = setTimeout(
      () => setSecondsUntilResend((seconds) => seconds - 1),
      1000
    )
    return () => clearTimeout(timeout)
  }, [secondsUntilResend])

  const resendEmail = async () => {
    try {
      const { data } = await claimVenueAccount({
        variables: { input: { email } },
      })
      const claimedVenueAccount = data?.claimVenueAccount

      // The account was claimed in the meantime
      if (!claimedVenueAccount) {
        onBackToSignIn()
        return
      }

      setSecondsUntilResend(claimedVenueAccount.resendAvailableInSeconds ?? 0)
      successToast({ description: t('signIn.claimAccount.emailResent') })
    } catch {
      errorToast({ description: t('TRANSLATION_135') })
    }
  }

  return (
    <Card maxW={400} m={2}>
      <VStack alignItems="center" spacing={4}>
        <Text
          width="full"
          textAlign="center"
          fontFamily="Cocogoose Pro"
          fontSize="2xl"
          color="brand.ultramarine"
        >
          {t('signIn.claimAccount.title')}
        </Text>
        <Text textAlign="center">
          {t('signIn.claimAccount.description', { integrationName })}
        </Text>
        <Text textAlign="center">
          {t('signIn.claimAccount.emailSent', { email })}
        </Text>
        <VStack width="full" alignItems="start" spacing={1}>
          <Text fontSize="sm" color="brand.darkBlueGray">
            {t('signIn.claimAccount.emailLabel')}
          </Text>
          <Text fontFamily="Proxima Nova Semibold" wordBreak="break-all">
            {email}
          </Text>
        </VStack>
        <VStack width="full" alignItems="start" spacing={1}>
          <Text fontSize="sm" color="brand.darkBlueGray">
            {t('signIn.claimAccount.integrationOriginLabel')}
          </Text>
          <Flex alignItems="center" gap={3}>
            <Box position="relative" width="96px" height="32px">
              <Image
                src={IntegrationImages[integrationType]}
                alt={integrationName}
                fill
                style={{ objectFit: 'contain', objectPosition: 'left' }}
              />
            </Box>
            <Text fontFamily="Proxima Nova Semibold">{integrationName}</Text>
          </Flex>
        </VStack>
        <Button
          width="full"
          mt="16px"
          variant="outline"
          isLoading={isClaimVenueAccountLoading}
          isDisabled={secondsUntilResend > 0}
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onClick={resendEmail}
        >
          {t('signIn.claimAccount.resendButtonLabel')}
        </Button>
        {secondsUntilResend > 0 && (
          <Text fontSize="sm" color="brand.darkBlueGray" textAlign="center">
            {t('signIn.claimAccount.resendAvailableIn', {
              seconds: secondsUntilResend,
            })}
          </Text>
        )}
        <Button
          variant="link"
          color="brand.ultramarine"
          onClick={onBackToSignIn}
        >
          {t('signIn.claimAccount.backToSignIn')}
        </Button>
      </VStack>
    </Card>
  )
}
